import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import { IAudioPlayer } from './interfaces';
import { BufferLoader } from './BufferLoader';

export const AudioPlayer: FC<IAudioPlayer> = ({
  startTime,
  endTime,
  noiseEvents,
  isPlaying,
  position,
  playbackSpeed = 1,
  languageData,
}) => {
  const audioContext: AudioContext = useMemo(() => new AudioContext(), []);
  const [, updatePlayingList] = useState({});
  const sourcePlaying = useRef<Record<number, boolean>>({});
  const audioBuffer = useRef<Record<number, AudioBuffer>>({});
  const sources = useRef<Record<number, AudioBufferSourceNode>>({});
  const timing = useRef<Record<number, { startTime: number; endTime: number }>>({});

  // Cleanup effect
  useEffect(() => {
    return () => {
      stopAll();
    };
  }, []);

  const addSource = (id: number) => {
    sources.current[id] = audioContext.createBufferSource();
    sources.current[id].buffer = audioBuffer.current[id];
    sources.current[id].playbackRate.value = playbackSpeed;
    sources.current[id].connect(audioContext.destination);
  };

  const playSound = (id: number, delay: number, start: number) => {
    addSource(id);
    sources.current[id].start(delay, start);
    sourcePlaying.current[id] = true;
    updatePlayingList({ ...sourcePlaying.current });
  };

  const stopSound = (id: number) => {
    if (sourcePlaying.current[id]) {
      sources.current[id].stop();
      sourcePlaying.current[id] = false;
      updatePlayingList({ ...sourcePlaying.current });
    }

    // create a new source so it's connected already
    addSource(id);
  };

  const finishedLoading = (bufferListLoaded: any) => {
    Object.values(bufferListLoaded).forEach(({ id, buffer }: any) => {
      audioBuffer.current[id] = buffer;
    });
  };

  const stopAll = () => {
    Object.entries(sourcePlaying.current).forEach(([id, playing]) => {
      if (playing) {
        stopSound(Number(id));
      }
    });
  };

  // setup audio context and buffer
  useEffect(() => {
    stopAll();
    sourcePlaying.current = {};
    noiseEvents.forEach(({ id, startTime, endTime }) => {
      const convertedStartTime = DateTime.fromISO(startTime).toSeconds();
      const convertedEndTime = DateTime.fromISO(endTime).toSeconds();
      timing.current[id] = { startTime: convertedStartTime, endTime: convertedEndTime };
      sourcePlaying.current[id] = false;
    });
    updatePlayingList({ ...sourcePlaying.current });

    const extractAudioFilesInfo = noiseEvents.map(
      ({ id, resourceUri: { authenticationScheme, uri } }) => {
        return {
          id,
          authenticationScheme,
          uri,
        };
      }
    );

    const bufferLoader: any = new BufferLoader(
      audioContext,
      extractAudioFilesInfo,
      finishedLoading
    );

    bufferLoader.load(languageData);
  }, [noiseEvents]);

  useEffect(() => {
    if (isPlaying) {
      playAudio();
    }
  }, [position]);

  useEffect(() => {
    if (!isPlaying) {
      stopAll();
    }
  }, [isPlaying]);

  const playAudio = () => {
    Object.entries(sourcePlaying.current).forEach(([id, playing]) => {
      if (!playing) {
        if (position >= timing.current[id].startTime && position <= timing.current[id].endTime) {
          playSound(Number(id), 0, position - timing.current[id].startTime);
        }
      } else {
        if (position > timing.current[id].endTime || position < timing.current[id].startTime) {
          stopSound(Number(id));
        } else {
          stopSound(Number(id));
          playSound(Number(id), 0, position - timing.current[id].startTime);
        }
      }
    });

    // at the end of track, stop all players
    if (endTime === position) {
      stopAll();
    }
  };

  return null;
};
